import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, Award, Heart, BookOpen, Crown } from "lucide-react";
import { useLang } from "../LanguageContext";
import { useSettings } from "../SettingsContext";
import { useCurrency } from "../CurrencyContext";
import { IMG, CATEGORY_IMG, CATEGORY_ORDER, BRAND, resolveSiteImage, catLabel } from "../brand";
import MarqueeStrip from "../components/MarqueeStrip";
import TestimonialsCarousel from "../components/TestimonialsCarousel";
import SplitText from "../components/SplitText";
import EditableText from "../components/EditableText";
import EditableImage from "../components/EditableImage";
import EditableI18n from "../components/EditableI18n";
import { translations } from "../i18n";

const T_EN = translations.en;
const T_FR = translations.fr;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const resolveImg = (u) => (!u ? "" : u.startsWith("http") ? u : `${BACKEND_URL}${u}`);

export default function Home() {
  const { t, lang } = useLang();
  const { settings } = useSettings();
  const { format } = useCurrency();
  const heroUrl = resolveSiteImage(settings, "hero");
  const studioUrl = resolveSiteImage(settings, "studio");
  const [services, setServices] = useState([]);
  const [journalPosts, setJournalPosts] = useState([]);

  useEffect(() => {
    axios.get(`${API}/services`).then((r) => setServices(r.data.services || [])).catch(() => {});
    axios.get(`${API}/journal?limit=3`).then((r) => setJournalPosts(r.data.posts || [])).catch(() => {});
  }, []);

  // pick one signature service per category for preview
  const featured = CATEGORY_ORDER.map((cat) => {
    const list = services.filter((s) => s.category === cat);
    return { category: cat, sample: list[0], count: list.length };
  });

  return (
    <main data-testid="home-page">
      {/* ============ HERO ============ */}
      <section className="relative min-h-[100vh] flex items-end overflow-hidden qw-grain qw-vignette">
        <img
          src={heroUrl}
          alt=""
          className="absolute inset-0 w-full h-full object-cover qw-fade-in"
        />
        <EditableImage imageKey="hero" label="Hero photo" pos="top-28 right-6" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-background/20" />
        <div className="absolute inset-0 bg-gradient-to-r from-background/70 via-transparent to-transparent" />

        <div className="relative z-10 px-6 md:px-12 lg:px-20 pb-24 pt-40 w-full">
          <div className="max-w-3xl qw-fade-up">
            <p className="qw-overline mb-8 flex items-center gap-3">
              <span className="h-px w-12 bg-primary inline-block" />
              <EditableText kEn="home_hero_overline_en" kFr="home_hero_overline_fr" label="Hero overline"
                fallback={{ en: T_EN.hero.overline, fr: T_FR.hero.overline }} />
            </p>
            <h1
              data-testid="hero-title"
              className="font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-8xl text-foreground font-light tracking-tight leading-[1.02] mb-10 text-balance"
            >
              <EditableText kEn="hero_title_en" kFr="hero_title_fr" label="Hero title">
                {(value, p) => (
                  <span {...p}>
                    <SplitText text={value} stagger={120} initialDelay={200} duration={1100} />
                  </span>
                )}
              </EditableText>
            </h1>
            <p className="text-base lg:text-lg text-muted-foreground max-w-xl leading-relaxed mb-12 font-light">
              <EditableText kEn="hero_subtitle_en" kFr="hero_subtitle_fr" label="Hero subtitle" />
            </p>
            <div className="flex flex-wrap items-center gap-5">
              <Link
                to="/booking"
                data-testid="hero-cta-primary"
                className="qw-btn qw-btn-primary"
              >
                <EditableI18n k="hero.ctaPrimary" label="Hero primary button" /> <ArrowUpRight size={16} strokeWidth={1.2} />
              </Link>
              <Link
                to="/about"
                data-testid="hero-cta-secondary"
                className="qw-btn qw-btn-outline"
              >
                <EditableI18n k="hero.ctaSecondary" label="Hero secondary button" />
              </Link>
            </div>
            <p className="mt-7 text-xs text-foreground/70 tracking-wider">
              <EditableText kEn="txt_home_consult_hint_en" kFr="txt_home_consult_hint_fr" label="Consultation hint"
                fallback={{ en: "Not sure where to start? ", fr: "Pas sûre par où commencer ? " }} />{" "}
              <Link
                to="/consultation"
                data-testid="hero-consultation-link"
                className="qw-link-underline text-primary uppercase tracking-[0.2em]"
              >
                <EditableText kEn="txt_home_consult_link_en" kFr="txt_home_consult_link_fr" label="Consultation link text"
                  fallback={{ en: "Take the 60-second consultation", fr: "Consultation IA · 60 sec" }} />
                <ArrowUpRight size={11} strokeWidth={1.2} className="inline ml-1" />
              </Link>
            </p>
          </div>

          {/* Decorative right column */}
          <div className="hidden lg:flex absolute bottom-24 right-20 flex-col items-end gap-3">
            <p className="qw-overline text-foreground/70"><EditableI18n k="hero.sigLabel" label="Hero signature label" /></p>
            <p className="font-serif text-7xl text-primary qw-shimmer-text italic">2025</p>
            <p className="text-xs text-muted-foreground tracking-[0.3em]">DAKAR · LONDON</p>
          </div>
        </div>
      </section>

      <MarqueeStrip />

      {/* ============ INTRO / MAISON ============ */}
      <section className="px-6 md:px-12 lg:px-20 py-32 md:py-48 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5 lg:sticky lg:top-32 self-start">
          <p className="qw-overline mb-6">
            <EditableText kEn="home_intro_overline_en" kFr="home_intro_overline_fr" label="Maison overline"
              fallback={{ en: T_EN.intro.overline, fr: T_FR.intro.overline }} />
          </p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-foreground text-balance mb-10">
            <EditableText kEn="home_intro_title_en" kFr="home_intro_title_fr" label="Maison title"
              fallback={{ en: T_EN.intro.title, fr: T_FR.intro.title }} />
          </h2>
          <Link
            to="/about"
            data-testid="intro-about-link"
            className="qw-link-underline text-[0.75rem] tracking-[0.28em] uppercase text-primary inline-flex items-center gap-2"
          >
            <EditableI18n k="intro.cta" label="Maison link" /> <ArrowUpRight size={14} strokeWidth={1.2} />
          </Link>
        </div>
        <div className="lg:col-span-7 flex flex-col gap-12">
          <p className="text-lg md:text-xl text-foreground/85 font-light leading-relaxed">
            <EditableText kEn="home_intro_body_en" kFr="home_intro_body_fr" label="Maison body"
              fallback={{ en: T_EN.intro.body, fr: T_FR.intro.body }} />
          </p>
          <div className="relative overflow-hidden h-[520px] qw-grain">
            <EditableImage imageKey="studio" label="Studio photo" />
            <img
              src={studioUrl}
              alt="Queensway Studio interior"
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-[2000ms] ease-out"
            />
          </div>
          <div className="grid grid-cols-3 gap-8 pt-8">
            <div>
              <Award size={22} strokeWidth={1} className="text-primary mb-3" />
              <p className="qw-num text-3xl text-foreground">UK</p>
              <p className="text-xs text-muted-foreground tracking-wider uppercase mt-1">London Certified</p>
            </div>
            <div>
              <Sparkles size={22} strokeWidth={1} className="text-primary mb-3" />
              <p className="qw-num text-3xl text-foreground">20+</p>
              <p className="text-xs text-muted-foreground tracking-wider uppercase mt-1">Bespoke rituals</p>
            </div>
            <div>
              <Heart size={22} strokeWidth={1} className="text-primary mb-3" />
              <p className="qw-num text-3xl text-foreground">1:1</p>
              <p className="text-xs text-muted-foreground tracking-wider uppercase mt-1">Private suite</p>
            </div>
          </div>
        </div>
      </section>

      <div className="qw-divider mx-12 lg:mx-20" />

      {/* ============ SERVICES PREVIEW ============ */}
      {settings.show_home_services !== false && (
      <section className="px-6 md:px-12 lg:px-20 py-32 md:py-40">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 items-end">
          <div className="lg:col-span-8">
            <p className="qw-overline mb-6">
              <EditableText kEn="home_services_overline_en" kFr="home_services_overline_fr" label="Services overline"
                fallback={{ en: T_EN.services.overline, fr: T_FR.services.overline }} />
            </p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-foreground text-balance">
              <EditableText kEn="home_services_title_en" kFr="home_services_title_fr" label="Services title"
                fallback={{ en: T_EN.services.title, fr: T_FR.services.title }} />
            </h2>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Link
              to="/services"
              data-testid="home-services-link"
              className="qw-link-underline text-[0.75rem] tracking-[0.28em] uppercase text-primary inline-flex items-center gap-2"
            >
              <EditableI18n k="services.viewAll" label="View all services link" /> <ArrowUpRight size={14} strokeWidth={1.2} />
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-border">
          {featured.map((f, idx) => (
            <Link
              key={f.category}
              to="/services"
              data-testid={`service-card-${f.category}`}
              className="group relative aspect-[3/4] overflow-hidden bg-background"
            >
              <img
                src={resolveSiteImage(settings, f.category) || CATEGORY_IMG[f.category]}
                alt={catLabel(settings, lang, f.category, t.services.categories[f.category])}
                className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-105 transition-all duration-[1200ms] ease-out"
              />
              <EditableImage imageKey={f.category} label="Photo" />
              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/30 to-transparent" />
              <div className="absolute inset-0 p-6 flex flex-col justify-between">
                <span className="font-serif text-primary qw-num text-2xl">
                  0{idx + 1}
                </span>
                <div>
                  <h3 className="font-serif text-2xl text-foreground mb-1">
                    <EditableText kEn={`cat_${f.category}_label_en`} kFr={`cat_${f.category}_label_fr`} label="Category name"
                      fallback={{ en: T_EN.services.categories[f.category], fr: T_FR.services.categories[f.category] }} />
                  </h3>
                  {f.sample && (
                    <p className="text-xs text-muted-foreground tracking-wider">
                      <EditableI18n k="services.from" label="'From' price label" />{" "}
                      <span className="text-primary qw-num">{format(f.sample.price_fcfa)}</span>
                    </p>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
      )}

      {/* ============ JOURNAL PREVIEW ============ */}
      {settings.show_home_journal !== false && journalPosts.length > 0 && (
        <section data-testid="home-journal-section" className="px-6 md:px-12 lg:px-20 py-24 md:py-32 border-t border-border">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 items-end">
            <div className="lg:col-span-8">
              <p className="qw-overline mb-6">
                <EditableText kEn="txt_home_journal_overline_en" kFr="txt_home_journal_overline_fr" label="Journal overline" fallback={{ en: "Studio Diary", fr: "Studio Diary" }} />
              </p>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-foreground text-balance">
                <EditableText kEn="home_journal_title_en" kFr="home_journal_title_fr" label="Journal section title"
                  fallback={{ en: "From the Journal.", fr: "Les dernières lectures." }} />
              </h2>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end">
              <Link to="/journal" data-testid="home-journal-link" className="qw-link-underline text-[0.75rem] tracking-[0.28em] uppercase text-primary inline-flex items-center gap-2">
                <EditableText kEn="txt_home_journal_readall_en" kFr="txt_home_journal_readall_fr" label="Read all link" fallback={{ en: "Read all", fr: "Tout lire" }} /> <ArrowUpRight size={14} strokeWidth={1.2} />
              </Link>
            </div>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {journalPosts.map((p) => (
              <li key={p.id}>
                <Link to={`/journal/${p.slug}`} data-testid={`home-journal-card-${p.slug}`} className="group block">
                  <div className="aspect-[4/5] overflow-hidden bg-muted mb-6">
                    {p.cover_image ? (
                      <img src={resolveImg(p.cover_image)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-accent/10">
                        <BookOpen size={26} strokeWidth={0.7} className="text-primary/60" />
                      </div>
                    )}
                  </div>
                  <h3 className="font-serif text-xl md:text-2xl text-foreground group-hover:text-primary transition-colors leading-tight text-balance">
                    {lang === "fr" ? p.title_fr : p.title_en}
                  </h3>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ============ TESTIMONIALS ============ */}
      {settings.show_home_testimonials !== false && <TestimonialsCarousel />}

      {/* ============ INNER CIRCLE PROMO ============ */}
      {settings.show_home_circle !== false && (
        <section data-testid="home-circle-section" className="relative px-6 md:px-12 lg:px-20 py-32 md:py-40 bg-primary/10 border-t border-primary/25 overflow-hidden">
          <div className="max-w-4xl">
            <p className="qw-overline mb-6 flex items-center gap-3 text-primary">
              <Crown size={14} strokeWidth={1.2} />
              <EditableText kEn="home_circle_overline_en" kFr="home_circle_overline_fr" label="Inner Circle overline"
                fallback={{ en: "Queensway Inner Circle", fr: "Queensway Inner Circle" }} />
            </p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-foreground text-balance mb-8">
              <EditableText kEn="home_circle_title_en" kFr="home_circle_title_fr" label="Inner Circle title"
                fallback={{
                  en: "An inner circle for our most devoted.",
                  fr: "Un cercle privé pour nos plus fidèles.",
                }} />
            </h2>
            <p className="text-base lg:text-lg text-muted-foreground max-w-2xl leading-relaxed mb-10">
              <EditableText kEn="home_circle_body_en" kFr="home_circle_body_fr" label="Inner Circle body"
                fallback={{
                  en: "An annual membership with privileged pricing on every treatment, priority booking when days fill up, and first access to new rituals and seasonal offers.",
                  fr: "Une adhésion annuelle avec tarifs privilégiés sur chaque soin, réservation prioritaire lorsque les journées sont complètes, et accès en avant-première aux nouveaux rituels et offres saisonnières.",
                }} />
            </p>
            <div className="flex flex-wrap gap-3 mb-12">
              <span className="text-[0.65rem] tracking-[0.2em] uppercase px-4 py-2 border border-primary/40 text-foreground/85">
                <EditableText kEn="home_circle_perk1_en" kFr="home_circle_perk1_fr" label="Circle perk 1"
                  fallback={{
                    en: `−${settings.membership_discount_pct ?? 10}% on all treatments`,
                    fr: `−${settings.membership_discount_pct ?? 10}% sur tous les soins`,
                  }} />
              </span>
              <span className="text-[0.65rem] tracking-[0.2em] uppercase px-4 py-2 border border-primary/40 text-foreground/85">
                <EditableText kEn="home_circle_perk2_en" kFr="home_circle_perk2_fr" label="Circle perk 2"
                  fallback={{ en: "Priority booking", fr: "Réservation prioritaire" }} />
              </span>
              <span className="text-[0.65rem] tracking-[0.2em] uppercase px-4 py-2 border border-primary/40 text-foreground/85">
                <EditableText kEn="home_circle_perk3_en" kFr="home_circle_perk3_fr" label="Circle perk 3"
                  fallback={{ en: "Early access & exclusive offers", fr: "Accès anticipé & offres exclusives" }} />
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-8">
              <Link to="/account" data-testid="home-circle-cta" className="qw-btn qw-btn-primary">
                <EditableText kEn="home_circle_cta_en" kFr="home_circle_cta_fr" label="Circle button label"
                  fallback={{ en: "Join the circle", fr: "Rejoindre le cercle" }} />
                <ArrowUpRight size={16} strokeWidth={1.2} />
              </Link>
              <p className="font-serif text-2xl text-primary qw-num">
                {Number(settings.membership_fee_fcfa ?? 150000).toLocaleString("fr-FR")} FCFA
                <span className="text-sm text-muted-foreground font-sans"> / {lang === "fr" ? "an" : "year"}</span>
              </p>
            </div>
          </div>
        </section>
      )}

      {/* ============ LOCATIONS CTA ============ */}
      <section className="relative px-6 md:px-12 lg:px-20 py-32 md:py-40 bg-muted/30 border-y border-border">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
          <div>
            <p className="qw-overline mb-4">Dakar</p>
            <h3 className="font-serif text-3xl md:text-4xl text-foreground mb-4">{BRAND.locationDakar}</h3>
            <p className="text-sm text-muted-foreground max-w-md leading-relaxed">
              <EditableText kEn="home_dakar_blurb_en" kFr="home_dakar_blurb_fr" label="Dakar blurb"
                fallback={{
                  en: "Our primary atelier — a discreet sanctuary in the heart of Dakar.",
                  fr: "Notre adresse principale, un écrin discret au cœur de Dakar.",
                }} />
            </p>
          </div>
          <div>
            <p className="qw-overline mb-4">London</p>
            <h3 className="font-serif text-3xl md:text-4xl text-foreground mb-4">{BRAND.locationLondon}</h3>
            <p className="text-sm text-muted-foreground max-w-md leading-relaxed">
              <EditableText kEn="home_london_blurb_en" kFr="home_london_blurb_fr" label="London blurb"
                fallback={{
                  en: "London appointments available on request — where our credentials were earned.",
                  fr: "Rendez-vous à Londres sur demande, où nos certifications ont été obtenues.",
                }} />
            </p>
          </div>
        </div>
        <div className="mt-16">
          <Link
            to="/booking"
            data-testid="home-final-cta"
            className="qw-btn qw-btn-primary"
          >
            <EditableI18n k="nav.bookNow" label="Book Now button" />
            <ArrowUpRight size={16} strokeWidth={1.2} />
          </Link>
        </div>
      </section>
    </main>
  );
}
